import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  guardedTransaction,
  initiatePrivileged,
  listingPurchaseAvailability,
  processTransactionSmsNotifications,
  queryUsersByBookmark,
  sendMarketplaceMessage,
  transactionLineItems,
} from '../../util/api';
import { applyPurchaseAvailabilityToListings } from '../../util/purchaseAvailability';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import {
  hasPermissionToInitiateTransactions,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { getProcess, isBookingProcessAlias } from '../../transactions/transaction';
import { fetchCurrentUser, setCurrentUserHasOrders } from '../../ducks/user.duck';
import { getParentId, getSizes } from '../../util/dataExtractors';
import * as listingVisibility from '../../util/listingVisibility';
import { withPrivateListingsExcluded } from '../../util/publicListingQuery';

const { UUID } = sdkTypes;
const {
  canDirectlyTransactOnListing,
  canTransactOnListing,
  filterPublicListings,
} = listingVisibility;
const MINUTE_IN_MS = 1000 * 60;
const SIMILAR_LISTINGS_DEFAULT_LIMIT = 8;
const SIMILAR_LISTINGS_QUERY_PAGE_SIZE = 24;

const triggerTransactionSmsNotifications = ({ transactionId, source, transition }) => {
  if (!transactionId) {
    return;
  }

  processTransactionSmsNotifications({ transactionId, source, transition }).catch(e => {
    log.warn(e, 'transaction-sms-direct-trigger-failed', {
      source,
      transactionId: transactionId?.uuid || transactionId,
      transition,
    });
  });
};

const BRAND_PUBLIC_DATA_FIELDS = [
  'brand',
  'brandOption',
  'dancewear_brand',
  'dancewear_brandOption',
  'costume_brand',
  'costume_brandOption',
  'shoe_brand',
  'shoe_brandOption',
];

const SIZE_PUBLIC_DATA_FIELDS = [
  'size',
  'sizeOption',
  'sizeLabel',
  'dancewear_size',
  'dancewear_sizeOption',
  'costume_size',
  'costume_sizeOption',
  'shoe_size',
  'shoe_sizeOption',
];

const getUuidValue = id => (typeof id === 'string' ? id : id?.uuid);

const getSiblingVariantIds = listing => {
  const sizes = Array.isArray(getSizes(listing)) ? getSizes(listing) : [];
  const siblingIds = sizes.map(size => getUuidValue(size?.id)).filter(Boolean);
  return Array.from(new Set(siblingIds));
};

const getVariantGroupId = listing => {
  const rawGroupId = listing?.attributes?.publicData?.variantGroupId;
  return typeof rawGroupId === 'string' && rawGroupId.trim().length > 0 ? rawGroupId.trim() : null;
};

export const createVariantListingsQueryParams = ({ listing, config }) => {
  const parentId = getParentId(listing);
  const variantGroupId = getVariantGroupId(listing);
  const currentListingId = getUuidValue(listing?.id);
  const siblingIds = getSiblingVariantIds(listing);

  if (!parentId && !variantGroupId && siblingIds.length === 0) {
    return null;
  }

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  const listingIds = Array.from(new Set([currentListingId, ...siblingIds].filter(Boolean)));

  return withPrivateListingsExcluded({
    ...(parentId
      ? { pub_parentId: parentId }
      : variantGroupId
      ? { pub_variantGroupId: variantGroupId }
      : { ids: listingIds }),
    perPage: 100,
    minStock: 1,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.listing': ['title', 'description', 'price', 'metadata', 'publicData', 'state'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  });
};

const normalizeFilterValues = value => {
  if (Array.isArray(value)) {
    return value.flatMap(v => normalizeFilterValues(v));
  }

  if (value && typeof value === 'object') {
    return normalizeFilterValues(
      value.values ??
        value.option ??
        value.value ??
        value.key ??
        value.id ??
        value.label ??
        value.name
    );
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }

  return value === null || typeof value === 'undefined' ? [] : [`${value}`];
};

const dedupeValues = values => {
  const seen = new Set();
  return values.filter(value => {
    const key = `${typeof value}:${value}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const firstMatchingPublicDataValues = (publicData, fields) => {
  for (const field of fields) {
    const values = dedupeValues(normalizeFilterValues(publicData?.[field]));
    if (values.length > 0) {
      return values;
    }
  }

  return [];
};

const sizeVariationValues = publicData => {
  const variations = Array.isArray(publicData?.sizes) ? publicData.sizes : [];
  return dedupeValues(
    variations.flatMap(variation =>
      normalizeFilterValues(variation?.size ?? variation?.sizeOption ?? variation?.value)
    )
  );
};

export const getSimilarListingFilterValues = publicData => {
  const brandValues = firstMatchingPublicDataValues(publicData, BRAND_PUBLIC_DATA_FIELDS);
  const directSizeValues = firstMatchingPublicDataValues(publicData, SIZE_PUBLIC_DATA_FIELDS);
  const sizeValues =
    directSizeValues.length > 0 ? directSizeValues : sizeVariationValues(publicData);

  return { brandValues, sizeValues };
};

export const createSimilarListingsFilterKey = ({ brandValues = [], sizeValues = [] }) => {
  const serialise = values =>
    values
      .map(value => `${typeof value}:${value}`)
      .sort()
      .join('|');

  const hasFilters = brandValues.length > 0 || sizeValues.length > 0;
  return hasFilters ? `brand=${serialise(brandValues)};size=${serialise(sizeValues)}` : '';
};

export const createSimilarListingsRequestCacheKey = ({ currentListingId, similarFilterKey }) => {
  const listingUuid = getUuidValue(currentListingId);
  return listingUuid && similarFilterKey ? `${listingUuid}|${similarFilterKey}` : '';
};

export const createSimilarListingsFilterPhases = ({ brandValues = [], sizeValues = [] }) => {
  const phases = [];
  const hasBrand = brandValues.length > 0;
  const hasSize = sizeValues.length > 0;

  if (hasBrand && hasSize) {
    brandValues.forEach(brand => {
      sizeValues.forEach(size => {
        phases.push({ pub_brand: brand, pub_size: size });
      });
    });
  }

  if (hasBrand) {
    brandValues.forEach(brand => {
      phases.push({ pub_brand: brand });
    });
  }

  if (hasSize) {
    sizeValues.forEach(size => {
      phases.push({ pub_size: size });
    });
  }

  const seen = new Set();
  return phases.filter(phase => {
    const key = JSON.stringify(phase);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = timeSlotsForDate => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

export const createAuthorListingsQueryParams = ({ authorId, config, perPage = 12 }) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return withPrivateListingsExcluded({
    authorId,
    perPage,
    page: 1,
    sort: '-createdAt',
    minStock: 1,
    stockMode: 'match-undefined',
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  });
};

export const createSimilarListingsQueryParams = ({
  config,
  filters,
  perPage = SIMILAR_LISTINGS_QUERY_PAGE_SIZE,
}) => {
  const listingImageConfig = config?.layout?.listingImage || {};
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;

  return withPrivateListingsExcluded({
    ...filters,
    perPage,
    page: 1,
    sort: '-createdAt',
    minStock: 1,
    stockMode: 'match-undefined',
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.listing': ['title', 'description', 'price', 'metadata', 'publicData', 'state'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  });
};

// ================ Async Thunks ================ //

//////////////////
// Show Listing //
//////////////////
const showListingPayloadCreator = async ({ listingId, config, isOwn = false }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Current user entity is fetched in a bit lazy fashion, since it's not tied to returned Promise chain.
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };
  dispatch(fetchCurrentUser(fetchCurrentUserOptions));

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-1600',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
    // Between scaled-large (1024px) and scaled-xlarge (2400px): sharp on most screens at about
    // half the file size of scaled-xlarge. Extra sizes of a photo cost no extra API credits.
    'imageVariant.scaled-1600': 'w:1600;h:1600;fit:scale',
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(async data => {
      const availability = await listingPurchaseAvailability({ listingIds: [listingId] });
      if (data?.data?.data) {
        data.data.data = applyPurchaseAvailabilityToListings([data.data.data], availability)[0];
      }
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      return data;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const showListingThunk = createAsyncThunk(
  'ListingPage/showListing',
  showListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  return dispatch(showListingThunk({ listingId, config, isOwn })).unwrap();
};

///////////////////
// Fetch Reviews //
///////////////////
export const fetchReviewsThunk = createAsyncThunk(
  'ListingPage/fetchReviews',
  ({ listingId }, { rejectWithValue, extra: sdk }) => {
    return sdk.reviews
      .query({
        listing_id: listingId,
        state: 'public',
        include: ['author', 'author.profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      })
      .then(response => {
        return denormalisedResponseEntities(response);
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

export const fetchReviews = listingId => (dispatch, getState, sdk) => {
  return dispatch(fetchReviewsThunk({ listingId })).unwrap();
};

///////////////////////////
// Fetch Author Listings //
///////////////////////////
const fetchAuthorListingsPayloadCreator = (
  { authorId, currentListingId, config },
  { dispatch, getState, rejectWithValue, extra: sdk }
) => {
  if (!authorId) {
    return Promise.resolve({ response: { data: { data: [] } }, currentListingId });
  }

  const params = createAuthorListingsQueryParams({
    authorId,
    config,
  });

  return sdk.listings
    .query(params)
    .then(response => {
      const viewerUserId = getState()?.user?.currentUser?.id?.uuid;
      const filteredListings = filterPublicListings(response?.data?.data || [], viewerUserId);
      if (response?.data) {
        response.data.data = filteredListings;
      }

      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return { response, currentListingId };
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const fetchAuthorListingsThunk = createAsyncThunk(
  'ListingPage/fetchAuthorListings',
  fetchAuthorListingsPayloadCreator
);

export const fetchAuthorListings = (authorId, currentListingId, config) => dispatch => {
  return dispatch(fetchAuthorListingsThunk({ authorId, currentListingId, config })).unwrap();
};

//////////////////////////
// Fetch Variant Listings //
//////////////////////////
const fetchVariantListingsPayloadCreator = async (
  { listing, config },
  { dispatch, getState, rejectWithValue, extra: sdk }
) => {
  const queryParams = createVariantListingsQueryParams({ listing, config });
  if (!queryParams) {
    return { listingIds: [] };
  }

  const listingFields = config?.listing?.listingFields;
  const sanitizeConfig = { listingFields };

  try {
    const response = await sdk.listings.query(queryParams);
    const viewerUserId = getState()?.user?.currentUser?.id?.uuid;
    const filteredListings = filterPublicListings(response?.data?.data || [], viewerUserId);
    const filteredListingIds = filteredListings.map(listing => listing?.id?.uuid).filter(Boolean);
    const availability = filteredListingIds.length
      ? await listingPurchaseAvailability({ listingIds: filteredListingIds })
      : { availabilityByListingId: {} };
    const listingsWithCurrentAvailability = applyPurchaseAvailabilityToListings(
      filteredListings,
      availability
    );
    if (response?.data) {
      response.data.data = listingsWithCurrentAvailability;
    }

    dispatch(addMarketplaceEntities(response, sanitizeConfig));
    const listingIds = listingsWithCurrentAvailability.map(l => l?.id).filter(Boolean);
    return { listingIds };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const fetchVariantListingsThunk = createAsyncThunk(
  'ListingPage/fetchVariantListings',
  fetchVariantListingsPayloadCreator
);

export const fetchVariantListings = (listing, config) => dispatch => {
  return dispatch(fetchVariantListingsThunk({ listing, config }));
};

////////////////////////////
// Fetch Similar Listings //
////////////////////////////
const fetchSimilarListingsPayloadCreator = async (
  {
    currentListingId,
    brandValues = [],
    sizeValues = [],
    config,
    cacheKey,
    limit = SIMILAR_LISTINGS_DEFAULT_LIMIT,
    perPage = SIMILAR_LISTINGS_QUERY_PAGE_SIZE,
  },
  { dispatch, getState, rejectWithValue, extra: sdk }
) => {
  const cachedListingIds = cacheKey
    ? getState()?.ListingPage?.similarListingsCache?.[cacheKey]
    : null;
  if (Array.isArray(cachedListingIds)) {
    return { listingIds: cachedListingIds, cacheKey };
  }

  const normalizedBrandValues = dedupeValues(brandValues.flatMap(v => normalizeFilterValues(v)));
  const normalizedSizeValues = dedupeValues(sizeValues.flatMap(v => normalizeFilterValues(v)));
  const filterPhases = createSimilarListingsFilterPhases({
    brandValues: normalizedBrandValues,
    sizeValues: normalizedSizeValues,
  });
  const currentListingUuid = getUuidValue(currentListingId);

  if (!currentListingUuid || filterPhases.length === 0) {
    return { listingIds: [], cacheKey };
  }

  const listingIds = [];
  const seenListingIds = new Set([currentListingUuid]);
  const listingFields = config?.listing?.listingFields;
  const sanitizeConfig = { listingFields };

  try {
    const viewerUserId = getState()?.user?.currentUser?.id?.uuid;
    // One search at a time, most specific first (brand and size, then brand, then size), stopping
    // once there are enough: each search is billed, and the first one often fills the row
    for (const filters of filterPhases) {
      const params = createSimilarListingsQueryParams({ config, filters, perPage });
      const response = await sdk.listings.query(params);
      dispatch(addMarketplaceEntities(response, sanitizeConfig));

      const listings = response?.data?.data || [];
      for (const listing of listings) {
        const listingUuid = getUuidValue(listing?.id);
        if (!listingUuid || seenListingIds.has(listingUuid)) {
          continue;
        }
        if (!canTransactOnListing(listing, viewerUserId)) {
          continue;
        }

        seenListingIds.add(listingUuid);
        listingIds.push(listing.id);
        if (listingIds.length >= limit) {
          break;
        }
      }

      if (listingIds.length >= limit) {
        break;
      }
    }

    return { listingIds, cacheKey };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const fetchSimilarListingsThunk = createAsyncThunk(
  'ListingPage/fetchSimilarListings',
  fetchSimilarListingsPayloadCreator,
  {
    condition: ({ cacheKey }, { getState }) => {
      if (!cacheKey) {
        return true;
      }

      const listingPageState = getState()?.ListingPage;
      const isSameRequestInFlight =
        listingPageState?.fetchSimilarListingsInProgress &&
        listingPageState?.fetchSimilarListingsCacheKey === cacheKey;

      return !isSameRequestInFlight;
    },
  }
);

export const fetchSimilarListings = params => dispatch => {
  return dispatch(fetchSimilarListingsThunk(params)).unwrap();
};

//////////////////////////////////////////
// Fetch Listing Cart Count (Bookmarks) //
//////////////////////////////////////////
const fetchSaveCountBySellerPayloadCreator = ({ listingId }, { rejectWithValue }) => {
  const id = getUuidValue(listingId);
  if (!id) {
    return Promise.resolve(0);
  }

  return queryUsersByBookmark({ id })
    .then(response => response?.totalCount || 0)
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchSaveCountBySellerThunk = createAsyncThunk(
  'ListingPage/fetchSaveCountBySeller',
  fetchSaveCountBySellerPayloadCreator
);

export const fetchSaveCountBySeller = listingId => dispatch => {
  return dispatch(fetchSaveCountBySellerThunk({ listingId }));
};

//////////////////////
// Fetch Time Slots //
//////////////////////

const timeSlotsRequest = createAsyncThunk(
  'ListingPage/timeSlotsRequest',
  (params, { extra: sdk }) => {
    return sdk.timeslots.query(params).then(response => {
      return denormalisedResponseEntities(response);
    });
  }
);

const fetchTimeSlotsPayloadCreator = ({ listingId, start, end, timeZone, options }, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

  // The maximum pagination page size for timeSlots is 500
  const extraParams = extraQueryParams || {
    perPage: 500,
    page: 1,
  };

  // For small time units, we fetch the data per date.
  // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
  if (useFetchTimeSlotsForDate) {
    const dateId = stringifyDateToISO8601(start, timeZone);
    const dateData = getState().ListingPage.timeSlotsForDate[dateId];
    const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
    const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
    if (hasRecentlyFetchedData) {
      return Promise.resolve(dateData?.timeSlots || []);
    }

    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(response => {
        return response.payload;
      })
      .catch(e => {
        return [];
      });
  } else {
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(response => {
        return response.payload;
      })
      .catch(e => {
        return [];
      });
  }
};

export const fetchTimeSlotsThunk = createAsyncThunk(
  'ListingPage/fetchTimeSlots',
  fetchTimeSlotsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const fetchTimeSlots = (listingId, start, end, timeZone, options) => (
  dispatch,
  getState,
  sdk
) => {
  return dispatch(fetchTimeSlotsThunk({ listingId, start, end, timeZone, options })).unwrap();
};

//////////////////
// Send Inquiry //
//////////////////
const sendInquiryPayloadCreator = (
  { listing, message, offerParams = null },
  { dispatch, getState, rejectWithValue }
) => {
  const hasRequestedOffer = !!offerParams?.isOffer && Number(offerParams?.offerPrice) > 0;

  const viewerUserId = getState()?.user?.currentUser?.id?.uuid;
  if (!canDirectlyTransactOnListing(listing, viewerUserId)) {
    const error = new Error('LISTING_PRIVATE');
    error.status = 403;
    error.statusText = 'LISTING_PRIVATE';
    error.data = {
      errors: [
        {
          status: 403,
          code: 'LISTING_PRIVATE',
          title: 'Listing is private.',
        },
      ],
    };
    return rejectWithValue(storableError(error));
  }

  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    return rejectWithValue(storableError(error));
  }

  const listingId = listing?.id;
  const [processName, alias] = processAlias.split('/');
  const transitions = getProcess(processName)?.transitions;
  const hasOffer = hasRequestedOffer;
  const transition = hasOffer
    ? offerParams?.autoAcceptEligible
      ? transitions?.TRANSITION_SEND_OFFER_AUTO_ELIGIBLE || transitions?.TRANSITION_SEND_OFFER
      : transitions?.TRANSITION_SEND_OFFER
    : transitions?.INQUIRE;

  if (!transition) {
    const error = new Error(`Unsupported transition for process: ${processName}`);
    log.error(error, 'listing-offer-transition-missing', {
      listingId: listing?.id?.uuid,
      processName,
      hasOffer,
    });
    return rejectWithValue(storableError(error));
  }

  const bodyParams = {
    transition,
    processAlias,
    params: hasOffer
      ? {
          listingId,
          protectedData: {
            offerParams,
          },
        }
      : { listingId },
  };
  // Normal buyer-to-seller inquiries use the local privileged endpoint so its
  // verified-phone and rate-limit guards run before a conversation is created.
  // Message-level account guards remain in /api/messages/send.
  const initiate = hasOffer
    ? guardedTransaction({ action: 'initiate', bodyParams, queryParams: {} })
    : initiatePrivileged({
        isSpeculative: false,
        orderData: {},
        bodyParams,
        queryParams: {
          include: ['provider'],
          expand: true,
        },
      });

  return initiate
    .then(response => {
      const transactionId = response.data.data.id;

      if (hasOffer) {
        triggerTransactionSmsNotifications({
          transactionId,
          source: 'ListingPage',
          transition,
        });
      }

      return sendMarketplaceMessage({
        transactionId,
        content: message,
        isOrderMessage: false,
        isOfferMessage: hasOffer,
        context: {
          source: 'ListingPage',
          listingId: listing?.id?.uuid || listing?.id || null,
          hasOffer,
          isOfferMessage: hasOffer,
          offerType: offerParams?.type || null,
        },
      }).then(messageResponse => {
        const moderationStatus =
          messageResponse?.moderationStatus || messageResponse?.data?.data?.attributes?.status;
        if (moderationStatus !== 'pendingReview') {
          dispatch(setCurrentUserHasOrders());
        }
        return transactionId;
      });
    })
    .catch(e => {
      const logFn = hasOffer ? log.error : log.warn;
      logFn(e, hasOffer ? 'listing-offer-submit-failed' : 'listing-inquiry-submit-failed', {
        listingId: listing?.id?.uuid || listing?.id || null,
        transition,
        hasOffer,
        offerType: offerParams?.type || null,
        offerPrice: offerParams?.offerPrice || null,
        errorStatus: e?.status || e?.statusCode || null,
        errorCode: e?.code || null,
        errorMessage: e?.message || e?.statusText || null,
      });
      return rejectWithValue(storableError(e));
    });
};

export const sendInquiryThunk = createAsyncThunk(
  'ListingPage/sendInquiry',
  sendInquiryPayloadCreator,
  {
    condition: (_args, { getState }) => !getState()?.ListingPage?.sendInquiryInProgress,
  }
);
// Backward compatible wrapper for the thunk
export const sendInquiry = (listing, message, offerParams = null) => (dispatch, getState, sdk) => {
  return dispatch(sendInquiryThunk({ listing, message, offerParams })).unwrap();
};

// Helper function for loadData call.
// Note: listing could be ownListing entity too
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && !!tz) {
    const { unitType, priceVariants, startTimeInterval } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
      ? 'hour'
      : 'day';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];
    const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
      return Math.min(min, priceVariant.bookingLengthInMinutes);
    }, Number.MAX_SAFE_INTEGER);

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = intervalAlign => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
        : null;
    };

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonthEnd, tz, options(startOfToday))),
      dispatch(fetchTimeSlots(listing.id, nextMonth, followingMonthEnd, tz, options(nextMonth))),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

//////////////////////////////////
// Fetch Transaction Line Items //
//////////////////////////////////
const fetchTransactionLineItemsPayloadCreator = (
  { orderData, listingId, isOwnListing },
  { rejectWithValue }
) => {
  return transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      return response.data;
    })
    .catch(e => {
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
        statusText: e.statusText,
      });
      return rejectWithValue(storableError(e));
    });
};

export const fetchTransactionLineItemsThunk = createAsyncThunk(
  'ListingPage/fetchTransactionLineItems',
  fetchTransactionLineItemsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  return dispatch(fetchTransactionLineItemsThunk({ orderData, listingId, isOwnListing })).unwrap();
};

// ================ Slice ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
  authorListingsIds: [],
  fetchAuthorListingsInProgress: false,
  fetchAuthorListingsError: null,
  variantListingsIds: [],
  fetchVariantListingsInProgress: false,
  fetchVariantListingsError: null,
  similarListingsIds: [],
  similarListingsCache: {},
  fetchSimilarListingsInProgress: false,
  fetchSimilarListingsError: null,
  fetchSimilarListingsRequestId: null,
  fetchSimilarListingsCacheKey: null,
  saveCountBySeller: 0,
  // The listing id whose page data the server already loaded (see loadData)
  loadedOnServerId: null,
};

const listingPageSlice = createSlice({
  name: 'ListingPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...action.payload };
    },
    markLoadedOnServer: (state, action) => {
      state.loadedOnServerId = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(showListingThunk.pending, (state, action) => {
        state.id = action.meta.arg.listingId;
        state.showListingError = null;
      })
      .addCase(showListingThunk.fulfilled, (state, action) => {
        // Data is handled by addMarketplaceEntities in the thunk
      })
      .addCase(showListingThunk.rejected, (state, action) => {
        state.showListingError = action.payload;
      })
      .addCase(fetchReviewsThunk.pending, state => {
        state.fetchReviewsError = null;
      })
      .addCase(fetchReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(fetchReviewsThunk.rejected, (state, action) => {
        state.fetchReviewsError = action.payload;
      })
      .addCase(fetchAuthorListingsThunk.pending, state => {
        state.fetchAuthorListingsError = null;
        state.fetchAuthorListingsInProgress = true;
      })
      .addCase(fetchAuthorListingsThunk.fulfilled, (state, action) => {
        const { response, currentListingId } = action.payload;
        const currentListingUuid = getUuidValue(currentListingId);
        const listings = response?.data?.data || [];

        state.authorListingsIds = listings
          .map(l => l.id)
          .filter(id => getUuidValue(id) && getUuidValue(id) !== currentListingUuid);
        state.fetchAuthorListingsInProgress = false;
      })
      .addCase(fetchAuthorListingsThunk.rejected, (state, action) => {
        state.fetchAuthorListingsError = action.payload;
        state.fetchAuthorListingsInProgress = false;
      })
      .addCase(fetchVariantListingsThunk.pending, state => {
        state.fetchVariantListingsError = null;
        state.fetchVariantListingsInProgress = true;
        state.variantListingsIds = [];
      })
      .addCase(fetchVariantListingsThunk.fulfilled, (state, action) => {
        state.variantListingsIds = action.payload?.listingIds || [];
        state.fetchVariantListingsInProgress = false;
      })
      .addCase(fetchVariantListingsThunk.rejected, (state, action) => {
        state.fetchVariantListingsError = action.payload;
        state.fetchVariantListingsInProgress = false;
        state.variantListingsIds = [];
      })
      .addCase(fetchSimilarListingsThunk.pending, (state, action) => {
        state.fetchSimilarListingsError = null;
        state.fetchSimilarListingsInProgress = true;
        state.fetchSimilarListingsRequestId = action.meta.requestId;
        state.fetchSimilarListingsCacheKey = action.meta.arg?.cacheKey || null;
        state.similarListingsIds = [];
      })
      .addCase(fetchSimilarListingsThunk.fulfilled, (state, action) => {
        if (state.fetchSimilarListingsRequestId !== action.meta.requestId) {
          return;
        }
        const listingIds = action.payload?.listingIds || [];
        const cacheKey = action.payload?.cacheKey;
        state.similarListingsIds = listingIds;
        if (cacheKey) {
          state.similarListingsCache[cacheKey] = listingIds;
        }
        state.fetchSimilarListingsInProgress = false;
        state.fetchSimilarListingsRequestId = null;
        state.fetchSimilarListingsCacheKey = null;
      })
      .addCase(fetchSimilarListingsThunk.rejected, (state, action) => {
        if (state.fetchSimilarListingsRequestId !== action.meta.requestId) {
          return;
        }
        state.fetchSimilarListingsError = action.payload;
        state.fetchSimilarListingsInProgress = false;
        state.fetchSimilarListingsRequestId = null;
        state.fetchSimilarListingsCacheKey = null;
      })
      .addCase(fetchSaveCountBySellerThunk.pending, state => {
        state.saveCountBySeller = 0;
      })
      .addCase(fetchSaveCountBySellerThunk.fulfilled, (state, action) => {
        state.saveCountBySeller = action.payload || 0;
      })
      .addCase(fetchSaveCountBySellerThunk.rejected, state => {
        state.saveCountBySeller = 0;
      })
      .addCase(fetchTimeSlotsThunk.pending, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          state.timeSlotsForDate = removeOutdatedDateData(state.timeSlotsForDate);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = null;
          state.timeSlotsForDate[dateId].fetchedAt = null;
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = true;
          state.timeSlotsForDate[dateId].timeSlots = [];
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = null;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = true;
        }
      })
      .addCase(fetchTimeSlotsThunk.fulfilled, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchedAt = new Date().getTime();
          state.timeSlotsForDate[dateId].timeSlots = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].timeSlots = action.payload;
        }
      })
      .addCase(fetchTimeSlotsThunk.rejected, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = action.payload;
        }
      })
      .addCase(sendInquiryThunk.pending, state => {
        state.sendInquiryInProgress = true;
        state.sendInquiryError = null;
      })
      .addCase(sendInquiryThunk.fulfilled, state => {
        state.sendInquiryInProgress = false;
        state.inquiryModalOpenForListingId = null;
      })
      .addCase(sendInquiryThunk.rejected, (state, action) => {
        state.sendInquiryInProgress = false;
        state.sendInquiryError = action.payload;
      })
      .addCase(fetchTransactionLineItemsThunk.pending, state => {
        state.fetchLineItemsInProgress = true;
        state.fetchLineItemsError = null;
      })
      .addCase(fetchTransactionLineItemsThunk.fulfilled, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.lineItems = action.payload;
      })
      .addCase(fetchTransactionLineItemsThunk.rejected, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.fetchLineItemsError = action.payload;
      });
  },
});

export const { setInitialValues, markLoadedOnServer } = listingPageSlice.actions;

export default listingPageSlice.reducer;

// ================ Load data ================ //

// Sharetribe listing IDs are UUIDs (e.g. 69faeb40-eecc-46c5-9a8f-34978cf6ac22).
// The ListingPageCanonical route (/l/:id) can be hit with a malformed URL where a
// slug or tracking parameter lands in the :id segment instead of a real UUID.
// Passing a non-UUID to sdk.listings.show() causes a 400 from Sharetribe, which
// bubbles up as a Rollbar "load-data-failed" error — noisy and unhelpful.
// We catch it early: if :id isn't a valid UUID we synthesise a 404 store error so
// the page renders <NotFoundPage>, and we return a resolved promise so Routes.js
// never sees a rejection and never logs to Rollbar.
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isBrowser = () => typeof window !== 'undefined';

// The server renders a listing page with the same data the browser would fetch right after, and
// every fetch is billed. When the server loaded this listing and the visitor isn't logged in (bots
// and most shoppers), the browser keeps the server's data. A logged-in visitor's page can differ
// (their own private listings), so it's fetched again for them.
const canReuseServerData = (params, state, sdk) => {
  const loadedOnServerId = state.ListingPage?.loadedOnServerId;
  if (!isBrowser() || !loadedOnServerId || loadedOnServerId !== params.id || params.variant) {
    return Promise.resolve(false);
  }
  return Promise.resolve(sdk.authInfo())
    .then(authInfo => authInfo?.isAnonymous !== false)
    .catch(() => false);
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  return canReuseServerData(params, getState(), sdk).then(reuse => {
    if (reuse) {
      dispatch(markLoadedOnServer(null));
      return Promise.resolve();
    }
    return loadListingPageData(params, search, config)(dispatch, getState, sdk);
  });
};

const loadListingPageData = (params, search, config) => (dispatch, getState, sdk) => {
  if (!UUID_REGEX.test(params.id)) {
    // Treat any non-UUID id as not-found: show <NotFoundPage> without hitting the API.
    dispatch(
      showListingThunk.rejected(null, 'loadData-invalid-id', null, {
        type: 'error',
        name: 'Error',
        message: 'Not Found',
        status: 404,
        statusText: 'Not Found',
        apiErrors: [],
      })
    );
    return Promise.resolve();
  }

  const listingId = new UUID(params.id);
  const state = getState();
  const currentUser = state.user?.currentUser;
  const inquiryModalOpenForListingId =
    isUserAuthorized(currentUser) && hasPermissionToInitiateTransactions(currentUser)
      ? state.ListingPage.inquiryModalOpenForListingId
      : null;

  // Clear old line-items
  dispatch(setInitialValues({ lineItems: null, inquiryModalOpenForListingId }));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const promises = hasNoViewingRights
    ? // If user has no viewing rights, only allow fetching their own listing without reviews
      [dispatch(showListing(listingId, config, true))]
    : // For users with viewing rights, fetch the listing and the associated reviews
      [dispatch(showListing(listingId, config)), dispatch(fetchReviews(listingId))];

  return Promise.all(promises).then(response => {
    const listingResponse = response[0];
    const listing = listingResponse?.data?.data;
    const authorId = listing?.relationships?.author?.data?.id;
    const followUpCalls = [];

    if (listing?.id) {
      followUpCalls.push(dispatch(fetchSaveCountBySeller(listing.id)));
      followUpCalls.push(dispatch(fetchVariantListings(listing, config)));
    }

    if (authorId) {
      followUpCalls.push(dispatch(fetchAuthorListings(authorId, listing?.id, config)));
    }

    const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
    if (isBookingProcessAlias(transactionProcessAlias) && !hasNoViewingRights) {
      // Fetch timeSlots if the user has viewing rights.
      // This can happen parallel to loadData.
      // We are not interested to return them from loadData call.
      followUpCalls.push(fetchMonthlyTimeSlots(dispatch, listing));
    }

    return Promise.all(followUpCalls).then(followUpResults => {
      // Async thunks resolve with a rejected action instead of throwing, so a part of the page
      // that failed on the server is fetched again in the browser
      const hasFailedPart = [...response.slice(1), ...followUpResults].some(
        result => result?.error || result?.meta?.requestStatus === 'rejected'
      );
      const isBookable = isBookingProcessAlias(transactionProcessAlias);
      if (!isBrowser() && listing?.id && !isBookable && !hasFailedPart) {
        dispatch(markLoadedOnServer(params.id));
      }
      return response;
    });
  });
};
